import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Note } from '../../types/learning';
import './NoteEditor.css';

// Pre-filled note content for quick note creation
interface PrefilledContent {
  content: string;
  relatedWord?: string;
  tags?: string[];
}

interface NoteEditorProps {
  note: Note | null;
  onSave: (noteData: Partial<Note>) => void;
  onClose: () => void;
  availableTags?: string[];
  associatedContent?: {
    type: 'vocabulary' | 'grammar' | 'segment';
    content: string;
  };
  prefilledContent?: PrefilledContent | null;
}

const NoteEditor: React.FC<NoteEditorProps> = ({
  note,
  onSave,
  onClose,
  availableTags = [],
  associatedContent,
  prefilledContent
}) => {
  const { t } = useTranslation('learning');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [relatedWord, setRelatedWord] = useState('');

  useEffect(() => {
    if (note) {
      // Editing existing note
      setContent(note.content || '');
      setTags(note.tags || []);
      setRelatedWord(note.relatedWord || '');
    } else if (prefilledContent) {
      // New note with pre-filled content
      setContent(prefilledContent.content || '');
      setTags(prefilledContent.tags || []);
      setRelatedWord(prefilledContent.relatedWord || '');
    } else {
      // Reset for new empty note
      setContent('');
      setTags([]);
      setRelatedWord('');
    }
  }, [note, prefilledContent]);

  const handleAddTag = (tag: string) => {
    const trimmedTag = tag.trim().toLowerCase();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag]);
      setNewTag('');
      setShowTagSuggestions(false);
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag(newTag);
    } else if (e.key === 'Escape') {
      setShowTagSuggestions(false);
    }
  };

  const handleSave = () => {
    if (!content.trim()) {
      alert(t('noteEditor.contentRequired'));
      return;
    }

    onSave({
      content: content.trim(),
      tags,
      relatedWord: relatedWord.trim() || undefined
    });
  };

  const filteredSuggestions = availableTags.filter(tag =>
    !tags.includes(tag) &&
    tag.toLowerCase().includes(newTag.toLowerCase())
  );

  return (
    <div className="note-editor-overlay" onClick={onClose}>
      <div className="note-editor-modal" onClick={(e) => e.stopPropagation()}>
        <div className="note-editor-header">
          <h2>{note ? t('noteEditor.editNote') : t('noteEditor.newNote')}</h2>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        {associatedContent && (
          <div className="associated-content">
            <div className="associated-content__label">
              {t('noteEditor.relatedTo', { type: associatedContent.type })}
            </div>
            <div className="associated-content__box">
              {associatedContent.content}
            </div>
          </div>
        )}

        <div className="note-editor-body">
          <div className="form-group">
            <label htmlFor="note-content">{t('noteEditor.noteContentRequired')}</label>
            <textarea
              id="note-content"
              className="note-textarea"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={t('noteEditor.notePlaceholder')}
              rows={8}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="related-word">{t('noteEditor.relatedWord')}</label>
            <input
              id="related-word"
              type="text"
              className="note-input"
              value={relatedWord}
              onChange={(e) => setRelatedWord(e.target.value)}
              placeholder={t('noteEditor.relatedWordPlaceholder')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="tags">{t('noteEditor.tags')}</label>
            <div className="tags-container">
              {tags.map(tag => (
                <span key={tag} className="tag-chip">
                  #{tag}
                  <button
                    className="tag-remove"
                    onClick={() => handleRemoveTag(tag)}
                  >
                    ✕
                  </button>
                </span>
              ))}
              <div className="tag-input-wrapper">
                <input
                  id="tags"
                  type="text"
                  className="tag-input"
                  value={newTag}
                  onChange={(e) => {
                    setNewTag(e.target.value);
                    setShowTagSuggestions(true);
                  }}
                  onKeyDown={handleTagInputKeyDown}
                  onFocus={() => setShowTagSuggestions(true)}
                  placeholder={t('noteEditor.addTag')}
                />
                {showTagSuggestions && newTag && filteredSuggestions.length > 0 && (
                  <div className="tag-suggestions">
                    {filteredSuggestions.slice(0, 5).map(suggestion => (
                      <button
                        key={suggestion}
                        className="tag-suggestion"
                        onClick={() => handleAddTag(suggestion)}
                      >
                        #{suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="tag-hints">
              {t('noteEditor.suggestedTags')}
            </div>
          </div>
        </div>

        <div className="note-editor-footer">
          <button className="button button--cancel" onClick={onClose}>
            {t('noteEditor.cancel')}
          </button>
          <button className="button button--save" onClick={handleSave}>
            {note ? t('noteEditor.updateNote') : t('noteEditor.saveNote')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NoteEditor;
